import { DateFilterOption } from "@/design-system/components/DateFilter/DateFilter";

export type QuestionBase = {
  id: string;
  text: string;
  position: number;
};

export type SingleChoiceQuestion = QuestionBase & {
  type: "single_choice";
  options: string[];
};

export type OpenTextQuestion = QuestionBase & {
  type: "open_text";
  placeholder: string;
};

export type YesNoQuestion = QuestionBase & {
  type: "yes_no";
};

export type RatingQuestion = QuestionBase & {
  type: "rating";
  scale: 5; // Fixed at 1-5 for MVP. PRD §6.3 specifies rating scale.
  lowLabel: string;
  highLabel: string;
};

export type NPSQuestion = QuestionBase & {
  type: "nps";
};

export type DistributionChartResult = Extract<
  QuestionResponseSummary,
  { questionType: "single_choice" | "yes_no" | "rating" }
>;
export type NpsChartResult = Extract<
  QuestionResponseSummary,
  { questionType: "nps" }
>;
export type OpenTextResultQuestion = Extract<
  QuestionResponseSummary,
  { questionType: "open_text" }
>;

export type DistributionEntry = { label: string; count: number };

export type NpsGrouping = {
  totalRespondents: number;
  pctRespondents: number | null;
};

export type NpsData = {
  score: number;
  promoters: NpsGrouping;
  demoters: NpsGrouping;
  passives: NpsGrouping;
  distribution: DistributionEntry[];
};

export type RatingData = {
  score: number;
  distribution: DistributionEntry[];
};

export type OpenTextData = {
  samples: string[];
};

export type DistributionData = {
  distribution: DistributionEntry[];
};

export type Question =
  | SingleChoiceQuestion
  | OpenTextQuestion
  | YesNoQuestion
  | RatingQuestion
  | NPSQuestion;

export type QuestionType = Question["type"];

// Human-readable labels for question types
export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  single_choice: "Single Choice",
  open_text: "Open Text",
  yes_no: "Yes / No",
  rating: "Rating Scale",
  nps: "NPS (Net Promoter Score)",
};

export type UrlMatchType = "contains" | "exact" | "starts_with" | "wildcard";

// Page constraint shared between page-mode and event-mode triggers.
//   - In page mode it IS the trigger (>=1 pattern required to publish).
//   - In event mode it scopes the trigger to a subset of pages
//     (`patterns` may be empty = "any page").
export type PageConstraint = {
  matchType: UrlMatchType;
  patterns: string[];
};

// Survey trigger configuration. Mirrors the `trigger` object on the
// backend API (back/app/surveys/survey_handler.go) and the SDK payload
// (back/models/evmeta/sdk_surveys.go). See OpenSpec change
// `survey-event-triggering` for the shape rationale.
export type SurveyTrigger =
  | {
      kind: "page";
      pageConstraint: PageConstraint;
    }
  | {
      kind: "event";
      eventNames: string[];
      // Optional page restriction. Empty `patterns` array means
      // "fire on any page".
      pageConstraint: PageConstraint;
    };

export type SurveyStatus = "draft" | "active" | "paused";

export type SurveyPosition =
  | "bottom_right"
  | "bottom_left"
  | "top_right"
  | "top_left";

export type SurveyTheme = {
  buttonColor: string;
  backgroundColor: string;
  textColor: string;
};

export type SurveyAppearance = {
  position: SurveyPosition;
  theme: SurveyTheme;
};

export const DEFAULT_SURVEY_APPEARANCE: SurveyAppearance = {
  position: "bottom_right",
  theme: {
    buttonColor: "#3F51B5",
    backgroundColor: "#FFFFFF",
    textColor: "#1E2125",
  },
};

// Human-readable labels for survey positions (builder UI).
export const SURVEY_POSITION_LABELS: Record<SurveyPosition, string> = {
  bottom_right: "Bottom right",
  bottom_left: "Bottom left",
  top_right: "Top right",
  top_left: "Top left",
};

export type Survey = {
  id: string;
  name: string;
  description: string;
  status: SurveyStatus;
  questions: Question[];
  trigger: SurveyTrigger;
  idleTimeSeconds: number;
  startDate: Date | null;
  endDate: Date | null;
  // Optional — only set when the user picks the "complete after N responses"
  // limit mode in the builder. TODO(CE-1283): backend persistence.
  responseLimit?: number | null;
  // Customizes where/how the survey widget renders at runtime
  // (position + theme colors). Persisted by the backend.
  appearance: SurveyAppearance;
  responseCount: number;
  createdAt: Date;
  updatedAt: Date;
  sourceConversationId: string | null;
};

type QuestionResponseSummaryBase = {
  questionId: string;
  questionIndex: number;
  questionPrompt: string;
  totalResponses: number;
};

export type QuestionResponseSummary =
  | (QuestionResponseSummaryBase & {
      questionType: "single_choice" | "yes_no";
      data: DistributionData;
    })
  | (QuestionResponseSummaryBase & {
      questionType: "rating";
      data: RatingData;
    })
  | (QuestionResponseSummaryBase & {
      questionType: "open_text";
      data: OpenTextData;
    })
  | (QuestionResponseSummaryBase & {
      questionType: "nps";
      data: NpsData;
    });

export type SurveyResultsData = {
  surveyId: string;
  // Total sessions where the survey was shown ($survey_shown).
  impressions: number;
  // Sessions that reached completion ($survey_completed).
  responses: number;
  // responses / impressions, or null when impressions == 0.
  responseRate: number | null;
  questionSummaries: QuestionResponseSummary[];
};

export type SurveyTimeseriesGranularity = "hour" | "day" | "week";

export type SurveyTimeseriesPoint = {
  bucket: Date;
  impressions: number;
  responses: number;
};

export type SurveyTimeseries = {
  granularity: SurveyTimeseriesGranularity;
  points: SurveyTimeseriesPoint[];
};

// One row per session in the Responses tab. Answers is a map keyed by
// question index (as a string, since JSON object keys are strings).
export type SurveyResponseRow = {
  deviceId: string;
  userId: string | null;
  startedAt: Date;
  completedAt: Date | null;
  pageUrl: string | null;
  answers: Record<string, string>;
};

export type SurveyResponsesPage = {
  rows: SurveyResponseRow[];
  total: number;
};

export type SurveyDateRange = {
  from: Date;
  to: Date;
};

// Validation helpers (`validateTrigger`, `validateSurveyForPublish`,
// `validatePatternFormat`) and the `useSurveyValidation` hook live in
// `front/src/surveys/hooks/useSurveyValidation.ts`. Import them from
// there directly — keeping this module type-only avoids a circular
// import (the hook imports types from here).

export const SURVEY_DATE_FILTERS: DateFilterOption[] = [
  { type: "today" as const, label: "Today", tooltip: "Today only" },
  { type: "7DaysAgo" as const, label: "7D", tooltip: "Last 7 complete days" },
  {
    type: "30DaysAgo" as const,
    label: "30D",
    tooltip: "Last 30 complete days",
  },
  {
    type: "3MonthsAgo" as const,
    label: "3M",
    tooltip: "Last 3 complete months",
  },
  {
    type: "6MonthsAgo" as const,
    label: "6M",
    tooltip: "Last 6 complete months",
  },
  {
    type: "12MonthsAgo" as const,
    label: "12M",
    tooltip: "Last 12 complete months",
  },
];
